import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ArrowDropDown as ArrowDropDownIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';
import ventasConfigService from '../../services/ventasConfigService';
import companiaService, { Compania } from '../../services/companiaService';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import SearchableSelect from '../../components/SearchableSelect';
import { resolverNombreCliente } from '../facturaventa/pos/clienteNombre';
import { getApiErrorMessage } from '../../utils/apiError';

const defaultForm: SucursalVenta = {
  nroSuc: 0,
  nombreSucursal: '',
  iniciarEn: '',
  tipoSuc: '',
  idCompania: '',
};

const SucursalVentaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroSuc } = useParams();
  const [formData, setFormData] = useState<SucursalVenta>({ ...defaultForm });
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [nroClientePred, setNroClientePred] = useState<number | null>(null);
  const [clientePredLabel, setClientePredLabel] = useState('');
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEdit = !!(nroSuc && nroSuc !== 'nuevo');

  useEffect(() => {
    (async () => {
      try {
        const items = await companiaService.getCompanias({ pageNumber: 1, pageSize: 200 });
        setCompanias(items);
        if (!isEdit && items.length === 1 && items[0]?.idCompania) {
          setFormData((prev) =>
            prev.idCompania ? prev : { ...prev, idCompania: items[0].idCompania },
          );
        }
      } catch {
        setCompanias([]);
      }
    })();
  }, [isEdit]);

  const loadSucursal = useCallback(async () => {
    if (!nroSuc || nroSuc === 'nuevo') return;
    try {
      setLoading(true);
      const nro = Number(nroSuc);
      const [sucursal, clientePred] = await Promise.all([
        sucursalesVentaService.getSucursalVenta(nro),
        ventasConfigService.getClientePredeterminado(nro).catch(() => null),
      ]);
      setFormData({
        nroSuc: sucursal.nroSuc,
        nombreSucursal: sucursal.nombreSucursal ?? '',
        iniciarEn: sucursal.iniciarEn ?? '',
        tipoSuc: sucursal.tipoSuc ?? '',
        idCompania: String(sucursal.idCompania ?? '').trim(),
      });
      if (clientePred?.nroCliente && clientePred.nroCliente > 0) {
        setNroClientePred(clientePred.nroCliente);
        const nombre = String(clientePred.nombre ?? '').trim();
        setClientePredLabel(
          nombre ? `${clientePred.nroCliente} - ${nombre}` : String(clientePred.nroCliente),
        );
      } else {
        setNroClientePred(null);
        setClientePredLabel('');
      }
    } catch {
      enqueueSnackbar('Error al cargar la sucursal', { variant: 'error' });
      navigate('/configuraciones/sucursalesventa');
    } finally {
      setLoading(false);
    }
  }, [nroSuc, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroSuc && nroSuc !== 'nuevo') loadSucursal();
  }, [nroSuc, loadSucursal]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroSuc') {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleClearClientePred = () => {
    setNroClientePred(null);
    setClientePredLabel('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      if (!formData.nombreSucursal.trim()) {
        enqueueSnackbar('El nombre de la sucursal es requerido', { variant: 'warning' });
        return;
      }

      if (companias.length >= 2 && !String(formData.idCompania ?? '').trim()) {
        enqueueSnackbar('Debe seleccionar la compañía del punto de venta', { variant: 'warning' });
        return;
      }

      const nroSucGuardar = isEdit ? Number(nroSuc) : formData.nroSuc;
      const payload: SucursalVenta = {
        ...formData,
        idCompania: String(formData.idCompania ?? '').trim() || undefined,
      };

      if (isEdit) {
        await sucursalesVentaService.updateSucursalVenta(Number(nroSuc), payload);
      } else {
        if (!formData.nroSuc || formData.nroSuc <= 0) {
          enqueueSnackbar('El N° de sucursal es requerido', { variant: 'warning' });
          return;
        }
        await sucursalesVentaService.createSucursalVenta(payload);
      }

      await ventasConfigService.setClientePredeterminado(nroSucGuardar, nroClientePred);

      enqueueSnackbar('Sucursal guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/sucursalesventa');
    } catch (error: unknown) {
      console.error('Error al guardar sucursal:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar la sucursal'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/configuraciones/sucursalesventa')}
              sx={{ textTransform: 'none' }}
            >
              Sucursales de Venta
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Editar Sucursal' : 'Nueva Sucursal'}
            </Typography>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField
                  name="nroSuc"
                  label="N° Sucursal"
                  type="number"
                  value={formData.nroSuc}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={isEdit}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  name="nombreSucursal"
                  label="Nombre"
                  value={formData.nombreSucursal}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchableSelect
                  label="Compañía"
                  required={companias.length >= 2}
                  allowEmpty={companias.length < 2}
                  emptyOptionLabel="Seleccione compañía"
                  value={formData.idCompania?.trim() || null}
                  onChange={(v) => setFormData((prev) => ({ ...prev, idCompania: v ?? '' }))}
                  options={companias.map((c) => {
                    const id = String(c.idCompania ?? '').trim();
                    const nombre = String(c.nombre ?? '').trim();
                    return {
                      value: id,
                      label: nombre ? `${id} — ${nombre}` : id,
                    };
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="tipoSuc"
                  label="Tipo Sucursal"
                  value={formData.tipoSuc || ''}
                  onChange={handleChange}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Cliente predeterminado"
                  value={clientePredLabel}
                  placeholder="Seleccionar cliente..."
                  fullWidth
                  helperText="Se usará automáticamente al emitir facturas en el POS de esta sucursal."
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {nroClientePred != null && nroClientePred > 0 ? (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearClientePred();
                            }}
                            edge="end"
                            size="small"
                            title="Quitar cliente"
                            aria-label="Quitar cliente"
                          >
                            <ClearIcon />
                          </IconButton>
                        ) : null}
                        <IconButton
                          onClick={() => setOpenClienteSearch(true)}
                          edge="end"
                          size="small"
                          title="Buscar cliente"
                          aria-label="Buscar cliente"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onClick={() => setOpenClienteSearch(true)}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2, cursor: 'pointer' },
                    '& .MuiInputBase-input': { cursor: 'pointer' },
                  }}
                />
              </Grid>
            </Grid>

            <ClienteSearch
              open={openClienteSearch}
              onClose={() => setOpenClienteSearch(false)}
              onSelect={(cliente) => {
                const nro = Number(cliente.nroCliente) || 0;
                const nombre =
                  resolverNombreCliente(cliente) ||
                  String(cliente.nombre ?? cliente.razonSocial ?? '').trim();
                setNroClientePred(nro > 0 ? nro : null);
                setClientePredLabel(nombre ? `${nro} - ${nombre}` : nro > 0 ? String(nro) : '');
                setOpenClienteSearch(false);
              }}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/configuraciones/sucursalesventa')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucursalVentaForm;
